import React, { useEffect, useRef, useState } from 'react';
import { Box, InputAdornment, Typography } from '@mui/material';
import { FormTextField } from './FormTextField';
import { FormFullWidth } from './FormLayout';
import { composeE164, parseStoredPhone } from '../../utils/phone';

export interface PhoneNumberFieldProps {
  value: string;
  onChange: (e164: string) => void;
  onValidationChange?: (error: string) => void;
  disabled?: boolean;
}

export const PhoneNumberField: React.FC<PhoneNumberFieldProps> = ({
  value,
  onChange,
  onValidationChange,
  disabled,
}) => {
  const [dialCode, setDialCode] = useState('1');
  const [nationalNumber, setNationalNumber] = useState('');
  const [error, setError] = useState('');
  const [legacyRaw, setLegacyRaw] = useState<string | null>(null);
  const lastEmitted = useRef('');
  const skipNextEmit = useRef(false);

  useEffect(() => {
    if (value === lastEmitted.current) return;

    const parsed = parseStoredPhone(value);
    skipNextEmit.current = true;
    setDialCode(parsed.dialCode);
    setNationalNumber(parsed.nationalNumber);
    setLegacyRaw(parsed.unparseable ? parsed.raw : null);
    setError('');
    onValidationChange?.('');

    if (parsed.e164) {
      lastEmitted.current = parsed.e164;
      if (parsed.e164 !== value) {
        onChange(parsed.e164);
      }
    } else {
      lastEmitted.current = value;
    }
  }, [value]);

  useEffect(() => {
    if (skipNextEmit.current) {
      skipNextEmit.current = false;
      return;
    }

    if (legacyRaw !== null) {
      setLegacyRaw(null);
    }

    const { value: e164, error: err } = composeE164(dialCode, nationalNumber);
    setError(err);
    onValidationChange?.(err);

    const digits = nationalNumber.replace(/\D/g, '');
    if (!digits) {
      if (lastEmitted.current !== '') {
        lastEmitted.current = '';
        onChange('');
      }
      return;
    }

    if (!err && e164 && e164 !== lastEmitted.current) {
      lastEmitted.current = e164;
      onChange(e164);
    }
  }, [dialCode, nationalNumber]);

  return (
    <FormFullWidth>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <FormTextField
          label="Code"
          name="phone_dial_code"
          value={dialCode}
          onChange={(e) => setDialCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
          disabled={disabled}
          error={Boolean(error) && !dialCode}
          sx={{ width: 110, flexShrink: 0 }}
          inputProps={{ inputMode: 'numeric', autoComplete: 'tel-country-code' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0 }}>
                +
              </InputAdornment>
            ),
          }}
        />
        <FormTextField
          label="Mobile Number"
          name="phone_national"
          value={nationalNumber}
          onChange={(e) => setNationalNumber(e.target.value.replace(/\D/g, ''))}
          disabled={disabled}
          error={Boolean(error)}
          helperText={error || 'Example: +1 xxxxxxxxxx (SMS format)'}
          placeholder="xxxxxxxxxx"
          inputProps={{ inputMode: 'tel', autoComplete: 'tel-national' }}
        />
      </Box>
      {legacyRaw && (
        <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
          Existing number is not in international format. Re-enter it to enable SMS OTP, or clear
          the field.
        </Typography>
      )}
    </FormFullWidth>
  );
};
